import { forwardRef } from 'react';
import styled from 'styled-components';
import { MediaImage } from '@elfsight/widget-media';
import { Popup } from '@elfsight/widget-modal';
import { CardContainer } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import type { Job } from '../../types';
import {
  Buttons,
  Description,
  getTitleBlockSettings,
  Skills,
  TitleBlock
} from '../layout/items/parts';
import { coverUrl } from '../utils';
import { usePopup } from './PopupProvider';

type PopupComponentProps = {
  jobs: Job[];
};

export const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>(
  ({ jobs }, forwardingRef) => {
    const { isShowPopup, hidePopup, jobId } = usePopup();
    const settings = useSettings();
    const {
      popupBackdropColor,
      popupBackgroundColor,
      popupTextColor,
      popupLinkColor,

      popupHeadlineColor,

      popupButtonColor,
      popupTitleStyle,
      popupSalaryStyle,
      popupCaptionStyle,
      popupButtonStyle,
      popupHeadlineStyle,
      popupDescriptionStyle,
      popupSkillStyle,
      isShowPopupApplyButton,
      isShowPopupSalary,
      isShowPopupCompany,
      isShowPopupDepartment,
      isShowPopupLocation,
      isShowPopupDescription,
      isShowPopupSkills,
      isShowPopupTypeOfContract,
      isShowJobCover,
      isShowJobLogo,

      popupBorderRadius
    } = settings;
    const currentJob: Partial<Job> = jobs.find((job) => job.id === jobId) ?? {};
    const { title, description, skills, link, cover } = currentJob;

    const img = coverUrl(cover, jobs);
    const withCover = isShowJobCover && !!img;

    return (
      <Popup
        ref={forwardingRef}
        width={600}
        show={isShowPopup}
        backdropColor={popupBackdropColor}
        margin={[40, 0]}
        onCloseRequest={hidePopup}
        withCloseControl
        closeControlPosition="outside"
        fixed
        fit
        disableScroll
        closeOnOutsideClick
        closeOnEscape
        withoutTransition
      >
        <Content>
          <Card
            padding={24}
            backgroundColor={popupBackgroundColor}
            _borderRadius={popupBorderRadius}
          >
            {withCover && <MediaImage src={img} alt={title} ratio={0.6667} />}

            <TitleBlock
              {...currentJob}
              {...getTitleBlockSettings(settings)}
              titleFontStyle={popupTitleStyle}
              captionFontStyle={popupCaptionStyle}
              salaryFontStyle={popupSalaryStyle}
              isShowSalary={isShowPopupSalary}
              isShowCompany={isShowPopupCompany}
              isShowDepartment={isShowPopupDepartment}
              isShowLocation={isShowPopupLocation}
              isShowTypeOfContract={isShowPopupTypeOfContract}
              isShowJobLogo={isShowJobLogo}
              salaryAlign="flex-start"
              color={popupTextColor}
              isPopup
              withCover={withCover}
            />

            {isShowPopupApplyButton && link && (
              <Buttons
                isShowLearnMoreButton={false}
                isShowApplyButton
                data={{ link }}
                fontSize={popupButtonStyle.fontSize}
                fontWeight={popupButtonStyle?.fontWeight}
                color={popupButtonColor}
                isPopup
              />
            )}

            {isShowPopupDescription && (
              <Description
                isShowTitle
                isClamp={false}
                color={popupTextColor}
                linkColor={popupLinkColor}
                fontSize={popupDescriptionStyle.fontSize}
                headlineStyle={popupHeadlineStyle}
                headlineColor={popupHeadlineColor}
              >
                {description}
              </Description>
            )}

            {isShowPopupSkills && !!skills?.length && (
              <Skills
                isPopup
                items={skills}
                color={popupTextColor}
                fontSize={popupSkillStyle.fontSize}
                headlineStyle={popupHeadlineStyle}
                headlineColor={popupHeadlineColor}
              />
            )}
          </Card>
        </Content>
      </Popup>
    );
  }
);

const Content = styled.div`
  position: relative;
  box-sizing: border-box;

  & * {
    box-sizing: border-box;
  }
`;

const Card = styled(CardContainer)<{ _borderRadius?: number }>`
  border-radius: ${({ _borderRadius }) => _borderRadius ?? 8}px;
`;
